import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useParams, useHistory } from "react-router-dom";
import {
  addImportBill,
  getImportBillById,
  updateImportBillPayments,
} from "../../../services/Import";
import {
  Grid,
  Paper,
  Typography,
  Divider,
  Box,
  Tooltip,
  IconButton,
  Button,
  Card,
  CardContent,
  Chip,
  useTheme,
  alpha,
  Badge,
  Alert,
} from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import TableItem from "../../../components/TableItem";
import CloseIcon from "@mui/icons-material/Close";

// Icons
import ReceiptIcon from "@mui/icons-material/Receipt";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import StoreIcon from "@mui/icons-material/Store";
import DescriptionIcon from "@mui/icons-material/Description";
import PersonIcon from "@mui/icons-material/Person";
import DiscountIcon from "@mui/icons-material/Discount";
import TagIcon from "@mui/icons-material/Tag";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ShoppingBasketIcon from "@mui/icons-material/ShoppingBasket";
import { getUser } from "../../../Utils/Common";
import PaymentListForConfimation from "../../../components/PaymentListForConfimation";

function Im_billConfirmation({ billData, setOpenConfirmation, payments }) {
  const theme = useTheme();
  const history = useHistory();
  const [importBill, setImportBill] = useState(null);
  const [rows, setRows] = useState([]);
  const [isLoading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const [displayAlert, setAlert] = useState(false);
  const [alertData, setAlertData] = useState({
    type: "",
    message: "",
  });

  const fetchData = async () => {
    setLoading(true);
    try {
      const result = billData;
      if (result) {
        setImportBill(result);
        const billItemSet = result.import_billItems || [];
        console.log("Bill Item set: ", billItemSet);
        const itemRows = billItemSet.map((item) => ({
          item: item.item,
          brand: item.brand,
          qty: item.qty,
          discountPerItem: item.discountPerItem,
          pricePerItem: item.unitPrice,
          totalPrice: item.price,
          billUnit: item.billUnit,
          unitType: item.unitType,
          itemUintsPerBillUnit: item.itemUintsPerBillUnit,
          isPriceUpdate: item.updatePrice ? "Yes" : "No",
        }));

        setRows(itemRows);
      } else {
        setErrors({
          ...errors,
          _billDataError: "Unable to fetch bill data",
        });
      }
    } catch (error) {
      console.error("Error fetching bill data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const submitBill = async () => {
    setLoading(true);
    const importBillItems = [];
    const selectedRows = billData.import_billItems;
    for (let x = 0; x < selectedRows.length; x++) {
      const item = {
        qty: parseFloat(selectedRows[x].qty),
        discount_perItem: parseFloat(selectedRows[x].discountPerItem),
        price: parseFloat(selectedRows[x].price),
        importId: selectedRows[x].importId,
        pricePerItem: parseFloat(selectedRows[x].unitPrice),
        billUnit: selectedRows[x].billUnit,
        prodUnitAmountPerBillUnit: parseFloat(
          selectedRows[x].itemUintsPerBillUnit,
        ),
        isPriceUpdate: selectedRows[x].updatePrice,
      };
      importBillItems.push(item);
    }

    //get user name
    let user = getUser();
    const billBody = {
      ...billData,
      import_billItems: importBillItems,
    };

    let result = await addImportBill(billBody);
    if (result.bill_id) {
      let createdBillData = result;
      console.log("Created Bill Data: ", createdBillData);
      await submitPayments(createdBillData.bill_id);
      setOpenConfirmation(false);
      setLoading(false);
      history.push("/template/im_bill_view/" + createdBillData.bill_id);
    } else {
      setErrors({
        ...errors,
        _billDataError: "Unable to submit bill data. " + result,
      });
      setLoading(false);
    }
  };

  const submitPayments = async (billId) => {
    if (payments && payments.length > 0) {
      try {
        await updateImportBillPayments(billId, payments);
      } catch (error) {
        console.error("Error submitting payment:", error);
      }
    }
  };

  const columns = [
    { id: "item", label: "Item", minWidth: 150 },
    { id: "brand", label: "Brand", minWidth: 120 },
    {
      id: "qty",
      label: "Qty",
      minWidth: 80,
      type: "number",
      isDecimal: false,
    },
    {
      id: "discountPerItem",
      label: "Discount",
      minWidth: 100,
      type: "number",
      isDecimal: false,
      format: (value) => `Rs: ${parseFloat(value || 0).toFixed(2)}`,
    },
    {
      id: "pricePerItem",
      label: "Price Per Item",
      minWidth: 120,
      type: "number",
      isDecimal: false,
      format: (value) => `Rs: ${parseFloat(value || 0).toFixed(2)}`,
    },
    {
      id: "totalPrice",
      label: "Total Price",
      minWidth: 120,
      type: "number",
      isDecimal: false,
      format: (value) => `Rs: ${parseFloat(value || 0).toFixed(2)}`,
    },
    { id: "billUnit", label: "Bill Unit", minWidth: 120 },
    { id: "unitType", label: "Using Unit", minWidth: 120 },
    {
      id: "itemUintsPerBillUnit",
      label: "Item Units Per Bill Unit",
      minWidth: 120,
    },
    {
      id: "isPriceUpdate",
      label: "Update Material Price",
      minWidth: 120,
    },
  ];

  const formatCurrency = (value) => {
    return typeof value === "number"
      ? `Rs: ${value.toFixed(2)}`
      : `Rs: ${parseFloat(value || 0).toFixed(2)}`;
  };

  const getTypeChip = (billType) => {
    let color;
    let bgColor;

    switch (billType) {
      case "RETURN BILL":
        color = theme.palette.warning.main;
        bgColor = alpha(theme.palette.warning.main, 0.1);
        break;
      case "INTAKE BILL":
        color = theme.palette.success.main;
        bgColor = alpha(theme.palette.success.main, 0.1);
        break;
      default:
        color = theme.palette.grey[600];
        bgColor = alpha(theme.palette.grey[600], 0.1);
    }

    return (
      <Chip
        label={billType}
        size="small"
        sx={{
          bgcolor: bgColor,
          color: color,
          fontWeight: 600,
          borderRadius: 1,
          px: 1,
        }}
      />
    );
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "70vh",
          flexDirection: "column",
          gap: 2,
        }}
      >
        <CircularProgress />
        <Typography color="text.secondary">Loading bill details...</Typography>
      </Box>
    );
  }

  if (errors._billDataError) {
    return (
      <Paper
        elevation={3}
        sx={{
          p: 4,
          m: 3,
          textAlign: "center",
          borderRadius: 2,
          backgroundColor: alpha(theme.palette.error.light, 0.1),
        }}
      >
        <Box sx={{ mb: 2 }}>
          <DescriptionIcon color="error" sx={{ fontSize: 48 }} />
        </Box>
        <Typography variant="h6" color="text.secondary" gutterBottom>
          {errors._billDataError}
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          Please retry in a few minutes or contact support.
        </Typography>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => setOpenConfirmation(false)}
        >
          Go Back
        </Button>
      </Paper>
    );
  }

  return (
    <Paper
      elevation={3}
      sx={{
        p: { xs: 2, md: 2 },
        m: { xs: 1, md: 2 },
        borderRadius: 2,
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <ReceiptIcon fontSize="large" color="primary" />
          <Typography variant="h5" fontWeight="600" color="primary.main">
            Import Bill Details
          </Typography>
        </Box>
        <Tooltip title="Close">
          <IconButton
            onClick={() => history.goBack()}
            aria-label="close"
            sx={{
              bgcolor: alpha(theme.palette.grey[500], 0.1),
              "&:hover": {
                bgcolor: alpha(theme.palette.grey[500], 0.2),
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Tooltip>
      </Box>

      <Divider sx={{ mb: 4 }} />

      {/* Bill Header Information */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <Card elevation={1} sx={{ height: "100%", borderRadius: 2 }}>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <TagIcon sx={{ mr: 1, color: "primary.main" }} />
                <Typography
                  variant="subtitle1"
                  fontWeight="700"
                  color="primary.main"
                >
                  Bill Information
                </Typography>
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Bill Number
                  </Typography>
                  <Typography variant="body1" fontWeight="600">
                    {importBill?.billNo}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Added By
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <PersonIcon
                      sx={{ fontSize: 16, color: "text.secondary" }}
                    />
                    <Typography variant="body1">
                      {importBill?.addedBy}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Shop
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <StoreIcon sx={{ fontSize: 16, color: "text.secondary" }} />
                    <Typography variant="body1">{importBill?.shop}</Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Bill Type
                  </Typography>
                  {getTypeChip(importBill?.billType)}
                </Grid>
                {importBill?.billDate ? (
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      Bill Date
                    </Typography>
                    <Typography variant="body1">
                      {
                        <Typography variant="body1">
                          {importBill?.billDate.toLocaleString("en-GB", {
                            timeZone: "Asia/Colombo",
                          })}
                        </Typography>
                      }
                    </Typography>
                  </Grid>
                ) : null}
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card elevation={1} sx={{ height: "100%", borderRadius: 2 }}>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <AttachMoneyIcon sx={{ mr: 1, color: "primary.main" }} />
                <Typography
                  variant="subtitle1"
                  fontWeight="700"
                  color="primary.main"
                >
                  Financial Details
                </Typography>
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      bgcolor: alpha(theme.palette.success.main, 0.1),
                      borderRadius: 2,
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Total Amount
                    </Typography>
                    <Typography
                      variant="h6"
                      fontWeight="700"
                      color="success.main"
                    >
                      {formatCurrency(importBill?.total)}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      bgcolor: alpha(theme.palette.warning.main, 0.1),
                      borderRadius: 2,
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Discount
                    </Typography>
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                    >
                      <DiscountIcon sx={{ color: "warning.main" }} />
                      <Typography
                        variant="h6"
                        fontWeight="700"
                        color="warning.main"
                      >
                        {formatCurrency(importBill?.discount)}
                      </Typography>
                    </Box>
                  </Paper>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Bill Note */}
      {importBill?.note && (
        <Card elevation={1} sx={{ mb: 4, borderRadius: 2 }}>
          <CardContent>
            <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
              <DescriptionIcon sx={{ mr: 1, color: "primary.main" }} />
              <Typography
                variant="subtitle1"
                fontWeight="700"
                color="primary.main"
              >
                Note
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ pl: 3, mt: 1 }}>
              {importBill?.note}
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* Bill Items Table */}
      <Card elevation={1} sx={{ mb: 4, borderRadius: 2, overflow: "hidden" }}>
        <CardContent>
          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
            <ShoppingBasketIcon sx={{ mr: 1, color: "primary.main" }} />
            <Typography
              variant="subtitle1"
              fontWeight="700"
              color="primary.main"
            >
              Bill Items
            </Typography>
            <Badge badgeContent={rows.length} color="primary" sx={{ ml: 1 }} />
          </Box>

          <Box
            sx={{
              bgcolor: alpha(theme.palette.background.default, 0.5),
              borderRadius: 1,
              overflow: "hidden",
            }}
          >
            <TableItem
              columns={columns}
              rows={rows}
              page={0}
              setPage={() => {}}
              tablePagin={false}
              totalPages={1}
              getData={fetchData}
            />
          </Box>
        </CardContent>
      </Card>

      {payments && payments.length > 0 && (
        <Card elevation={1} sx={{ mb: 4, borderRadius: 2 }}>
          <CardContent>
            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
              <AttachMoneyIcon sx={{ mr: 1, color: "primary.main" }} />
              <Typography
                variant="subtitle1"
                fontWeight="700"
                color="primary.main"
              >
                Payment History
              </Typography>
            </Box>
            <PaymentListForConfimation payments={payments} />
          </CardContent>
        </Card>
      )}

      {/* Action Buttons */}
      {displayAlert && (
        <Grid>
          <Alert
            severity={alertData.type}
            onClose={() => setAlert(false)}
            sx={{ mb: 2 }}
          >
            {alertData.message}
          </Alert>
        </Grid>
      )}
      <Box sx={{ display: "flex", justifyContent: "space-between" }}>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => setOpenConfirmation(false)}
        >
          Back To Edit
        </Button>
        <Button variant="contained" onClick={() => submitBill()}>
          Submit
        </Button>
      </Box>
    </Paper>
  );
}

export default Im_billConfirmation;
